/**
 * The bank-statement import's email lookup: every archived GT email in the statement's span.
 *
 * THIS READ FEEDS A WRITE. `ImportTransactionsSheet` matches each bank row to its email to get the
 * transaction's time of day, and saves the row with that time. The old read had no order and no
 * paging, so a statement spanning more than Max rows of emails (about 24 arrive a day, so about 42
 * days of statement) got an arbitrary 1,000 of them. A row whose email was cut off was saved at
 * 00:00:00. A row whose email was cut off but which had one look-alike left (same amount, value date
 * and type) took the look-alike through `findGtMatch`'s fallback, and was saved with another
 * payment's time. Its error was dropped too, so a failed read imported every row at midnight.
 *
 * WHAT THIS CHANGES AND WHAT IT DOES NOT. It returns every email in the span, in `id` order, and it
 * throws when any page fails or the rows fall short of the server's count. The sheet's catch turns
 * that into a "Failed to process files" toast before anything is staged or written. The matcher,
 * what a row is saved with, and how the rows are written are unchanged.
 *
 * THE ORDER IS THE EMAIL'S ID. Paging needs a unique key. It is also the order `findGtMatch` walks
 * the candidates in, which matters only when two emails share amount, value date, type and a
 * matching description; production holds 21 such groups (67 emails) that differ only in their time.
 * The old read handed them over in heap order, which can change between runs (3,452 of 3,792 rows
 * sit out of id order). `id` is the order they arrived in, and it is the same on every run.
 */

import { supabase } from '@/integrations/supabase/client';
import { fetchAllRows } from '@/lib/fetchAllRows';

export interface TransactionEmail {
    id: number;
    doc_number: string | null;
    amount: number;
    currency: string;
    type: string;
    time: string;
    value_date: string | null;
    description: string | null;
}

/** Every email whose value date falls in `minDate`..`maxDate` (inclusive 'yyyy-MM-dd'), by `id`. */
export async function fetchTransactionEmails(minDate: string, maxDate: string): Promise<TransactionEmail[]> {
    return fetchAllRows<TransactionEmail>({
        label: 'archived bank emails for the statement',
        orderedBy: 'id',
        page: (from, to) =>
            supabase
                .from('transaction_emails')
                .select('id, doc_number, amount, currency, type, time, value_date, description', { count: 'exact' })
                .gte('value_date', minDate)
                .lte('value_date', maxDate)
                .order('id', { ascending: true })
                .range(from, to),
        keyOf: (email) => email.id,
    });
}
